import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import Modal from '../../components/Modal/Modal'; // Ensure you import the Modal component
import { extractYouTubeVideoId } from '../../util/dataExtractors';
import { mixPanelAnalytics } from '../../analytics/handlers';
import './videoPlayer.css';
import css from './VimePlayer.module.css';
import { STORAGE_VIDEO_KEY } from '../../util/constants';
import { v4 as uuid } from 'uuid';
import { getDefaultLocale } from '../../util/genericHelpers';
import { useHistory } from 'react-router-dom';
import Icons from '../Icons/Icons';
const Player = lazy(() => import('@vime/react').then(mod => ({ default: mod.Player })));
const DefaultUi = lazy(() => import('@vime/react').then(mod => ({ default: mod.DefaultUi })));
const Youtube = lazy(() => import('@vime/react').then(mod => ({ default: mod.Youtube })));
const ClickToPlay = lazy(() => import('@vime/react').then(mod => ({ default: mod.ClickToPlay })));
const Spinner = lazy(() => import('@vime/react').then(mod => ({ default: mod.Spinner })));
const Poster = lazy(() => import('@vime/react').then(mod => ({ default: mod.Poster })));
const DblClickFullscreen = lazy(() =>
  import('@vime/react').then(mod => ({ default: mod.DblClickFullscreen }))
);
const VideoPlayer = ({
  videoURL = 'https://youtu.be/C10H3pLRm1I',
  poster,
  title,
  viewport,
  slug,
  resumeTime,
}) => {
  const playerRef = useRef(null);
  const modalPlayerRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInitialPlay, setIsInitialPlay] = useState(true); // Track first play
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal open state
  const parentRef = useRef(null); // Ref for the parent element
  const [parentHeight, setParentHeight] = useState(undefined); // State to store parent's height
  const [causeEffect, setCauseEffect] = useState(0);
  const [currentTime, setCurrentTime] = useState(0); // State to track current time
  const [hasFullyWatched, setHasFullyWatched] = useState(false);
  // const [playerClosedEarly, setPlayerClosedEarly] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [pausedTime, setPausedTime] = useState(0);
  const isMobileLayout = viewport.width > 0 && viewport.width < 1024;
  const lang = getDefaultLocale();
  const handleOverlayClick = () => {
    // mixPanelAnalytics.trackEvent('Video Played', { video_id: videoURL });
    setIsPlaying(true);
    setIsInitialPlay(false); // Mark as played
  };
  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    if (modalPlayerRef.current) {
      setPausedTime(modalPlayerRef.current.currentTime || 0);
    }
  };
  useEffect(() => {
    parentRef.current = document.querySelector('.rs-modal-body');
    if (parentRef?.current) {
      const inlineMaxHeight = parentRef.current.style.maxHeight; // Access inline style directly
      if (inlineMaxHeight !== '') {
        setParentHeight(inlineMaxHeight); // Update state
      } else {
        setCauseEffect(causeEffect => causeEffect + 1);
      }
    }
  }, [parentRef.current, causeEffect, viewport.width]);
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_VIDEO_KEY);
    const storedArray = stored ? JSON.parse(stored) : [];
    const videoPath = history?.location?.pathname;
    if (!hasFullyWatched && currentTime > 0) {
      const updatedArray = [...storedArray];
      const existingIndexVideo = updatedArray.findIndex(v => v.modalSlug === slug);
      const existingIndexTitle = updatedArray.findIndex(v => v.title === title);
      if (existingIndexVideo === -1 && existingIndexTitle == -1) {
        // No match by path — add new entry
        updatedArray.push({
          id: uuid(),
          title,
          path: videoPath,
          type: 'video',
          timePlayed: currentTime,
          added: new Date(),
          lang,
          isModal: true,
          modalSlug: slug,
        });
      } else {
        updatedArray[existingIndexVideo] = {
          ...updatedArray[existingIndexVideo],
          timePlayed: currentTime,
          added: new Date(),
        };
      }
      localStorage.setItem(STORAGE_VIDEO_KEY, JSON.stringify(updatedArray));
    }
    if (hasFullyWatched) {
      const filteredArray = storedArray.filter(v => v.modalSlug !== slug);
      localStorage.setItem(STORAGE_VIDEO_KEY, JSON.stringify(filteredArray));
    }
  }, [currentTime, hasFullyWatched, title, slug]);
  const handleTimeUpdate = event => {
    const time = event.detail;
    setCurrentTime(time);
    // check if video is fully watched by user
    if (videoDuration && event.detail >= videoDuration - 1) {
      setHasFullyWatched(true);
      // setPlayerClosedEarly(false);
    } else {
      // setPlayerClosedEarly(true);
    }
  };
  const handlePlayerReady = async () => {
    try {
      const player = playerRef.current;
      if (player) {
        setTimeout(async () => {
          player.currentTime = resumeTime;
          // player.play();
        }, 500);
      }
    } catch (err) {
      console.warn('Error setting start time:', err);
    }
  };
  return (
    <div
      id="video-container"
      className={classNames(css.videoContentWrapper)}
      data-testid="video-player-container"
    >
      {!isPlaying && pausedTime <= 0 ? (
        <>
          <img
            src={poster}
            alt={title}
            className={css.videoPoster}
            onClick={handleOverlayClick}
            data-testid="video-player-poster"
          />
          <div
            className={css.overlayIcon}
            onClick={handleOverlayClick}
            data-testid="video-player-play-button"
          >
            <Icons name="play" />
          </div>
        </>
      ) : (
        <div
          className={classNames(css.videoContainer, {
            [css.playingVideoContainer]: isPlaying,
          })}
        >
          <Suspense
            fallback={
              <div className={css.iconSpinner}>
                <span className={css.loader}></span>
              </div>
            }
          >
            <Player
              playsinline
              // autoplay={isInitialPlay}
              ref={playerRef}
              onVmPausedChange={event => {
                if (event.detail && isPlaying) {
                  setPausedTime(playerRef.current?.currentTime || 0);
                  setIsPlaying(false);
                }
              }}
              onVmPlayingChange={event => {
                if (event.detail && !isPlaying) {
                  if (pausedTime > 0 && playerRef.current) {
                    playerRef.current.currentTime = pausedTime;
                  }
                  setIsPlaying(true);
                }
              }}
              onVmFullscreenChange={event => {
                if (event.detail) {
                } else {
                }
              }}
              onVmCurrentTimeChange={handleTimeUpdate}
              onVmReady={handlePlayerReady}
              onVmDurationChange={e => setVideoDuration(e.detail)}
              onVmEnded={() => {
                setIsPlaying(false);
                setPausedTime(0);
              }}
            >
              <Youtube
                videoId={extractYouTubeVideoId(videoURL)}
                showFullscreenControl={true}
                params={{
                  modestbranding: 1,
                  rel: 0,
                  showinfo: 0,
                  vq: 'hd1080',
                  controls: 1,
                  disablekb: 1,
                  fs: 1,
                  iv_load_policy: 3,
                  start: resumeTime,
                }}
              />
              <DefaultUi>
                <ClickToPlay />
                <Spinner />
                <Poster />
                <DblClickFullscreen useOnMobile />
              </DefaultUi>
            </Player>
          </Suspense>
        </div>
      )}
      {isMobileLayout && (
        <div className={css.fullscreenTrigger} role="button" onClick={openModal}>
          {intl.formatMessage({
            id: 'VideoPlayer.showFullscreen',
          })}
        </div>
      )}
      {isMobileLayout && (
        <Modal
          id="VideoPlayerMobileModal"
          isOpen={isModalOpen}
          onClose={closeModal}
          onManageDisableScrolling={() => {}}
          usePortal
          isFullScreenVideoModal={true}
        >
          <div
            className={classNames(css.modalContent, {
              [css.rotatedVideo]: isModalOpen, // Add rotation class conditionally
            })}
          >
            <Suspense
              fallback={
                <div className={css.iconSpinner}>
                  <span className={css.loader}></span>
                </div>
              }
            >
              <Player
                onVmCurrentTimeChange={handleTimeUpdate}
                onVmPausedChange={event => {
                  if (event.detail && isPlaying) {
                    const current = modalPlayerRef.current?.currentTime || 0;
                    setPausedTime(current);
                    setIsPlaying(false);
                  }
                }}
                onVmPlayingChange={event => {
                  if (event.detail && !isPlaying) {
                    if (pausedTime > 0 && modalPlayerRef.current) {
                      modalPlayerRef.current.currentTime = pausedTime;
                    }
                    setIsPlaying(true);
                  }
                }}
                onVmEnded={() => {
                  setIsPlaying(false);
                  setPausedTime(0);
                }}
                playsinline
                autoplay
                ref={playerRef}
              >
                <Youtube
                  videoId={extractYouTubeVideoId(videoURL)}
                  showFullscreenControl={true}
                  params={{
                    modestbranding: 1,
                    rel: 0,
                    showinfo: 0,
                    vq: 'hd1080', // Request 1080p quality
                    start: resumeTime,
                    controls: 1,
                    disablekb: 1,
                    fs: 1,
                    iv_load_policy: 3,
                  }}
                />
                <DefaultUi>
                  <ClickToPlay />
                  <Spinner />
                  <Poster />
                  <DblClickFullscreen useOnMobile />
                </DefaultUi>
              </Player>
            </Suspense>
          </div>
        </Modal>
      )}
    </div>
  );
};
export default VideoPlayer;
